/* eslint-disable no-unused-vars, react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAdminAuth } from '../../../context/AdminAuthContext';
import inventoryApi from '../../../services/inventoryApi';
import InventoryLayout, { ActionButton } from '../../../components/admin/InventoryLayout';
import UpdateStockModal from './components/UpdateStockModal';

// Brand accent color
const ACCENT = '#7d6633';

// Glass morphism Card component
const Card = ({ children, className = '' }) => (
  <div
    className={
      'rounded-2xl shadow-lg border border-white/20 dark:border-white/10 bg-white/70 dark:bg-zinc-900/60 backdrop-blur-xl ' +
      '' + className
    }
  >
    {children}
  </div>
);

// Card Header component
const CardHeader = ({ title, icon, action, subtitle }) => (
  <div className="flex items-center justify-between p-4 md:p-5 border-b border-white/10 dark:border-white/10">
    <div className="flex items-center gap-3">
      <div className="h-10 w-10 rounded-xl bg-[--accent]/10 flex items-center justify-center" style={{ '--accent': ACCENT }}>
        {icon}
      </div>
      <div>
        <h3 className="font-semibold text-zinc-900 dark:text-zinc-100">{title}</h3>
        {subtitle && (
          <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5">{subtitle}</p>
        )}
      </div>
    </div>
    {action}
  </div>
);

const Products = () => {
  const { user, hasPermission } = useAdminAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState({});
  const [categories, setCategories] = useState([]);
  const [showSupplierModal, setShowSupplierModal] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [mobileStockStatus, setMobileStockStatus] = useState('all');
  const [saving, setSaving] = useState(false);
  const [notification, setNotification] = useState(null);
  const [filters, setFilters] = useState({
    query: '',
    category: '',
    lowStock: false,
    status: 'all' // Show ALL products by default (active + draft + archived)
  });


  useEffect(() => {
    loadItems();
    loadCategories();
  }, [filters]);

  const loadItems = async () => {
    try {
      setLoading(true);
      setError('');

      const params = {
        page: 1,
        pageSize: 25,
        query: filters.query,
        category: filters.category,
        lowStock: filters.lowStock,
        // Don't pass status if 'all' - let backend show everything
        status: filters.status === 'all' ? undefined : filters.status
      };

      const response = await inventoryApi.items.getItems(params);
      setItems(response.items || []);
      setPagination(response.pagination || {});

    } catch (err) {
      console.error('Error loading items:', err);
      setError('Failed to load items');
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const response = await inventoryApi.categories.getCategories();
      setCategories(response.categories || []);
    } catch (err) {
      console.error('Error loading categories:', err);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleAddItem = () => {
    navigate('/admin/inventory/products/add');
  };

  const handlePageChange = (newPage) => {
    setFilters(prev => ({ ...prev, page: newPage }));
  };

  const handleAddSupplier = () => {
    setShowSupplierModal(true);
  };

  const handleSaveSupplier = async (supplierData) => {
    try {
      await inventoryApi.suppliers.createSupplier(supplierData);
      setShowSupplierModal(false);
      // Optionally reload suppliers or show success message
    } catch (err) {
      console.error('Error saving supplier:', err);
    }
  };

  const handleCancelSupplier = () => {
    setShowSupplierModal(false);
  };

  // Notification helper
  const showNotification = (message, type = 'info') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 5000);
  };

  // Bulk Actions Functions
  const handleSelectItem = (itemId) => {
    setSelectedItems(prev => {
      const newSelection = prev.includes(itemId) 
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId];
      setShowBulkActions(newSelection.length > 0);
      return newSelection;
    });
  };

  const handleSelectAll = () => {
    if (selectedItems.length === items.length) {
      setSelectedItems([]);
      setShowBulkActions(false);
    } else {
      setSelectedItems(items.map(item => item.id));
      setShowBulkActions(true);
    }
  };

  const handleBulkArchive = async () => {
    try {
      setSaving(true);
      const results = [];
      
      for (const itemId of selectedItems) {
        try {
          await inventoryApi.items.archiveItem(itemId);
          results.push({ id: itemId, success: true });
        } catch (err) {
          const item = items.find(i => i.id === itemId);
          results.push({ 
            id: itemId, 
            success: false, 
            error: `Failed to archive ${item?.name || 'item'}` 
          });
        }
      }
      
      const successful = results.filter(r => r.success);
      const failed = results.filter(r => !r.success);
      
      if (successful.length > 0) {
        showNotification(`Successfully archived ${successful.length} item(s)`, 'success');
      }
      
      if (failed.length > 0) {
        console.error('Failed to archive items:', failed);
        showNotification(`Failed to archive ${failed.length} item(s): ${failed.map(f => f.error).join(', ')}`, 'error');
      }
      
      setSelectedItems([]);
      setShowBulkActions(false);
      await loadItems();
    } catch (err) {
      console.error('Error archiving items:', err);
      showNotification('An unexpected error occurred while archiving items.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleBulkDelete = async () => {
    try {
      setSaving(true);
      const results = [];
      
      // Delete all selected items - no checking, user confirmed
      for (const itemId of selectedItems) {
        try {
          await inventoryApi.items.deleteItem(itemId);
          results.push({ id: itemId, success: true });
        } catch (err) {
          const item = items.find(i => i.id === itemId);
          results.push({ 
            id: itemId, 
            success: false,
            itemName: item?.name,
            error: `Failed to delete ${item?.name || 'item'}: ${err.response?.data?.error || err.message}` 
          });
        }
      }
      
      // Show results
      const successful = results.filter(r => r.success);
      const failed = results.filter(r => !r.success);
      
      if (successful.length > 0 && failed.length === 0) {
        showNotification(`✅ Successfully deleted ${successful.length} product(s)!`, 'success');
      } else if (successful.length > 0 && failed.length > 0) {
        showNotification(
          `⚠️ Deleted ${successful.length} product(s), but ${failed.length} failed. Check console for details.`, 
          'error'
        );
      } else if (failed.length > 0) {
        showNotification(
          `❌ Failed to delete ${failed.length} product(s). Check console for details.`, 
          'error'
        );
        console.error('Delete errors:', failed);
      }
      
      setSelectedItems([]);
      setShowBulkActions(false);
      setShowDeleteConfirm(false);
      
      // Reload to show updated list
      await loadItems();
    } catch (err) {
      console.error('Error deleting items:', err);
      showNotification('❌ An unexpected error occurred while deleting items.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleBulkEdit = () => {
    // For now, just navigate to edit the first selected item
    if (selectedItems.length > 0) {
      navigate(`/admin/inventory/products/${selectedItems[0]}/edit`);
    }
  };

  // Status badge helper
  const StatusBadge = ({ status }) => {
    const statusConfig = {
      active: {
        bg: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
        label: 'Active',
        icon: (
          <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
        )
      },
      draft: {
        bg: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
        label: 'Draft',
        icon: (
          <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
            <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
          </svg>
        )
      },
      archived: {
        bg: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
        label: 'Archived',
        icon: (
          <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
            <path d="M4 3a2 2 0 100 4h12a2 2 0 100-4H4z" />
            <path fillRule="evenodd" d="M3 8h14v7a2 2 0 01-2 2H5a2 2 0 01-2-2V8zm5 3a1 1 0 011-1h2a1 1 0 110 2H9a1 1 0 01-1-1z" clipRule="evenodd" />
          </svg>
        )
      }
    };

    const config = statusConfig[status] || statusConfig.draft;

    return (
      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${config.bg}`}>
        {config.icon}
        {config.label}
      </span>
    );
  };

  // Bulk status change
  const handleBulkStatusChange = async (newStatus) => {
    try {
      setSaving(true);
      const results = [];
      
      for (const itemId of selectedItems) {
        try {
          await inventoryApi.items.changeItemStatus(itemId, newStatus);
          results.push({ id: itemId, success: true });
        } catch (err) {
          const item = items.find(i => i.id === itemId);
          results.push({ 
            id: itemId, 
            success: false, 
            error: `Failed to change status for ${item?.name || 'item'}` 
          });
        }
      }
      
      const successful = results.filter(r => r.success);
      const failed = results.filter(r => !r.success);
      
      if (successful.length > 0) {
        showNotification(`Successfully changed status for ${successful.length} item(s) to ${newStatus}`, 'success');
      }
      
      if (failed.length > 0) {
        console.error('Failed to change status:', failed);
        showNotification(`Failed to change status for ${failed.length} item(s)`, 'error');
      }
      
      setSelectedItems([]);
      setShowBulkActions(false);
      await loadItems();
    } catch (err) {
      console.error('Error changing status:', err);
      showNotification('An unexpected error occurred while changing status.', 'error');
    } finally {
      setSaving(false);
    }
  };

  // Status chip component
  const StatusChip = ({ onHand, reorderPoint }) => {
    if (onHand === 0) {
      return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">Out</span>;
    } else if (onHand <= reorderPoint) {
      return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">Low</span>;
    } else {
      return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">OK</span>;
    }
  };

  // Skeleton loader for table rows
  const SkeletonRow = () => (
    <tr>
      {[...Array(7)].map((_, i) => (
        <td key={i} className="px-6 py-4 whitespace-nowrap">
          <div className="animate-pulse">
            <div className="h-4 bg-gray-300 rounded w-full"></div>
          </div>
        </td>
      ))}
    </tr>
  );

  const getStockStatus = (quantity) => {
    if (quantity <= 0) return 'out';
    if (quantity <= 10) return 'low';
    return 'in';
  };

  const getStockConfig = (quantity) => {
    const status = getStockStatus(quantity);
    if (status === 'out') {
      return { label: 'Out of Stock', color: 'text-red-600', chip: 'bg-red-100 text-red-800' };
    }
    if (status === 'low') {
      return { label: 'Low Stock', color: 'text-orange-600', chip: 'bg-orange-100 text-orange-800' };
    }
    return { label: 'In Stock', color: 'text-emerald-600', chip: 'bg-emerald-100 text-emerald-800' };
  };

  const mobileItems = items.filter((item) => {
    if (mobileStockStatus === 'all') return true;
    const status = getStockStatus(item.totalStock || 0);
    return status === mobileStockStatus;
  });

  return (
    <InventoryLayout 
      title="Products" 
      subtitle="Manage your inventory products and stock levels"
      currentTab="products"
      action={
        (hasPermission('inventory.create') || user?.role === 'shop_admin' || user?.role === 'superadmin') && (
          <div className="hidden md:flex items-center gap-3">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="px-4 py-2 rounded-xl border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-700 dark:text-zinc-300 hover:bg-white/70 dark:hover:bg-zinc-800/70 transition-all text-sm font-medium"
            >
              Export
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="px-4 py-2 rounded-xl border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-700 dark:text-zinc-300 hover:bg-white/70 dark:hover:bg-zinc-800/70 transition-all text-sm font-medium"
            >
              Import
            </motion.button>
            <motion.button 
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleAddSupplier}
              className="px-4 py-2 rounded-xl border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-700 dark:text-zinc-300 hover:bg-white/70 dark:hover:bg-zinc-800/70 transition-all text-sm font-medium"
              style={{ borderColor: ACCENT, color: ACCENT }}
            >
              Add supplier
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleAddItem}
              className="px-4 py-2 rounded-xl text-white font-medium flex items-center gap-2 transition-all duration-200 hover:shadow-lg text-sm"
              style={{ background: ACCENT }}
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add product
            </motion.button>
          </div>
        )
      }
    >
      {/* Mobile Header - Like Bookings Page */}
      <div className="md:hidden mb-6 pt-2">
        <div className="flex items-start gap-3 mb-4">
          <div className="h-12 w-12 rounded-xl bg-[--accent]/10 flex items-center justify-center flex-shrink-0" style={{ '--accent': ACCENT }}>
            <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: ACCENT }}>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
            </svg>
          </div>
          <div className="flex-1">
            <h1 className="text-xl font-bold text-zinc-900 dark:text-zinc-100">Products</h1>
            <p className="text-xs text-zinc-600 dark:text-zinc-400 mt-1">
              Manage your inventory products and stock levels
            </p>
          </div>
        </div>
        
        {/* Mobile Action Button */}
        {(hasPermission('inventory.create') || user?.role === 'shop_admin' || user?.role === 'superadmin') && (
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={handleAddItem}
            className="inline-flex px-3 py-2 rounded-xl text-white text-sm font-medium items-center gap-1.5 transition-all duration-200 hover:shadow-lg whitespace-nowrap"
            style={{ background: ACCENT }}
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            Create
          </motion.button>
        )}
      </div>

      <div className="space-y-6">
        {error && (
          <Card className="border-red-200 dark:border-red-800 bg-red-50/50 dark:bg-red-900/20">
            <div className="p-4 md:p-5">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-xl bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                  <svg className="h-5 w-5 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-semibold text-red-900 dark:text-red-100">Error</h3>
                  <p className="text-sm text-red-700 dark:text-red-300 mt-0.5">{error}</p>
                </div>
              </div>
            </div>
          </Card>
        )}

        {/* Notification */}
        {notification && (
          <Card className={`${
            notification.type === 'success' 
              ? 'border-green-200 dark:border-green-800 bg-green-50/50 dark:bg-green-900/20'
              : notification.type === 'error'
              ? 'border-red-200 dark:border-red-800 bg-red-50/50 dark:bg-red-900/20'
              : 'border-blue-200 dark:border-blue-800 bg-blue-50/50 dark:bg-blue-900/20'
          }`}>
            <div className="p-4 md:p-5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`h-10 w-10 rounded-xl flex items-center justify-center ${
                    notification.type === 'success' 
                      ? 'bg-green-100 dark:bg-green-900/30'
                      : notification.type === 'error'
                      ? 'bg-red-100 dark:bg-red-900/30'
                      : 'bg-blue-100 dark:bg-blue-900/30'
                  }`}>
                    <svg className={`h-5 w-5 ${
                      notification.type === 'success' 
                        ? 'text-green-600 dark:text-green-400'
                        : notification.type === 'error'
                        ? 'text-red-600 dark:text-red-400'
                        : 'text-blue-600 dark:text-blue-400'
                    }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      {notification.type === 'success' ? (
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      ) : notification.type === 'error' ? (
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                      ) : (
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      )}
                    </svg>
                  </div>
                  <p className={`text-sm font-medium ${
                    notification.type === 'success' 
                      ? 'text-green-900 dark:text-green-100'
                      : notification.type === 'error'
                      ? 'text-red-900 dark:text-red-100'
                      : 'text-blue-900 dark:text-blue-100'
                  }`}>{notification.message}</p>
                </div>
                <motion.button 
                  onClick={() => setNotification(null)}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  className="text-lg font-bold hover:opacity-70 transition-opacity"
                >
                  ×
                </motion.button>
              </div>
            </div>
          </Card>
        )}

        {/* Mobile Search + Filters */}
        <Card className="md:hidden">
          <div className="p-4">
            <div className="flex items-center gap-3">
              <div className="relative flex-1">
                <svg className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-zinc-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                  type="text"
                  value={filters.query}
                  onChange={(e) => handleFilterChange('query', e.target.value)}
                  placeholder="Search products..."
                  className="w-full pl-10 pr-4 py-3 rounded-xl border border-white/20 dark:border-white/10 bg-white/60 dark:bg-zinc-800/60 backdrop-blur-sm text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-[--accent]/20 focus:border-[--accent]/30 transition-all min-h-[44px]"
                  style={{ '--accent': ACCENT }}
                />
              </div>
              <motion.button
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                onClick={() => setShowMobileFilters(true)}
                className="min-h-[44px] min-w-[44px] rounded-xl border border-white/20 dark:border-white/10 bg-white/60 dark:bg-zinc-800/60 backdrop-blur-sm text-zinc-700 dark:text-zinc-300 hover:bg-white/70 dark:hover:bg-zinc-800/70 transition-all flex items-center justify-center"
                style={{ '--accent': ACCENT }}
                aria-label="Open filters"
              >
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M7 12h10M10 18h4" />
                </svg>
              </motion.button>
            </div>
          </div>
        </Card>

        {/* Filters */}
        <Card className="hidden md:block">
          <CardHeader
            title="Filters"
            subtitle="Filter products by status, category, or search"
            icon={
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: ACCENT }}>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
              </svg>
            }
          />
          <div className="p-4 md:p-5">
            {/* Status Tabs - Shopify Style */}
            <div className="flex items-center space-x-2 mb-4 border-b border-white/10 dark:border-white/10">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => handleFilterChange('status', 'all')}
                className={`px-4 py-2 text-sm font-medium border-b-2 transition-all ${
                  filters.status === 'all'
                    ? 'border-[--accent] text-[--accent]'
                    : 'border-transparent text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100'
                }`}
                style={{ '--accent': ACCENT }}
              >
                All
                <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-400">
                  {items.length}
                </span>
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => handleFilterChange('status', 'active')}
                className={`px-4 py-2 text-sm font-medium border-b-2 transition-all ${
                  filters.status === 'active'
                    ? 'border-[--accent] text-[--accent]'
                    : 'border-transparent text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100'
                }`}
                style={{ '--accent': ACCENT }}
              >
                Active
                <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">
                  {items.filter(item => item.status === 'active').length}
                </span>
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => handleFilterChange('status', 'draft')}
                className={`px-4 py-2 text-sm font-medium border-b-2 transition-all ${
                  filters.status === 'draft'
                    ? 'border-[--accent] text-[--accent]'
                    : 'border-transparent text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100'
                }`}
                style={{ '--accent': ACCENT }}
              >
                Draft
                <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300">
                  {items.filter(item => item.status === 'draft').length}
                </span>
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => handleFilterChange('status', 'archived')}
                className={`px-4 py-2 text-sm font-medium border-b-2 transition-all ${
                  filters.status === 'archived'
                    ? 'border-[--accent] text-[--accent]'
                    : 'border-transparent text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100'
                }`}
                style={{ '--accent': ACCENT }}
              >
                Archived
                <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300">
                  {items.filter(item => item.status === 'archived').length}
                </span>
              </motion.button>
            </div>

            {/* Search and Category Filters */}
            <div className="flex items-center space-x-3">
              <div className="relative flex-1">
                <svg className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-zinc-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                  type="text"
                  value={filters.query}
                  onChange={(e) => handleFilterChange('query', e.target.value)}
                  placeholder="Search products..."
                  className="w-full pl-10 pr-4 py-3 rounded-xl border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-[--accent]/20 focus:border-[--accent]/30 transition-all"
                  style={{ '--accent': ACCENT }}
                />
              </div>
              <select
                value={filters.category}
                onChange={(e) => handleFilterChange('category', e.target.value)}
                className="px-4 py-3 rounded-xl border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-[--accent]/20 focus:border-[--accent]/30 transition-all"
                style={{ '--accent': ACCENT }}
              >
                <option value="">All categories</option>
                {categories.map(category => (
                  <option key={category} value={category}>
                    {category.charAt(0).toUpperCase() + category.slice(1)}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </Card>

        {/* Bulk Actions Bar */}
        {showBulkActions && (
          <Card className="border-blue-200 dark:border-blue-800 bg-blue-50/50 dark:bg-blue-900/20">
            <div className="p-4 md:p-5">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={selectedItems.length === items.length}
                      onChange={handleSelectAll}
                      className="rounded border-zinc-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm font-medium text-zinc-900 dark:text-zinc-100">
                      {selectedItems.length} selected
                    </span>
                  </div>
                </div>
                <div className="flex items-center space-x-3 flex-wrap gap-2">
                  {/* Status Change Buttons */}
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => handleBulkStatusChange('active')}
                    disabled={saving}
                    className="px-4 py-2 rounded-xl border border-emerald-200 dark:border-emerald-800 bg-emerald-50/50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-100/50 dark:hover:bg-emerald-900/30 transition-all text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    ✅ Set Active
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => handleBulkStatusChange('draft')}
                    disabled={saving}
                    className="px-4 py-2 rounded-xl border border-gray-200 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-900/20 text-gray-700 dark:text-gray-300 hover:bg-gray-100/50 dark:hover:bg-gray-900/30 transition-all text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    ✎ Set Draft
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => handleBulkStatusChange('archived')}
                    disabled={saving}
                    className="px-4 py-2 rounded-xl border border-orange-200 dark:border-orange-800 bg-orange-50/50 dark:bg-orange-900/20 text-orange-700 dark:text-orange-300 hover:bg-orange-100/50 dark:hover:bg-orange-900/30 transition-all text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    📦 Deactivate (Archive)
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setShowDeleteConfirm(true)}
                    disabled={saving}
                    className="px-4 py-2 rounded-xl border border-red-200 dark:border-red-800 bg-red-50/50 dark:bg-red-900/20 text-red-700 dark:text-red-300 hover:bg-red-100/50 dark:hover:bg-red-900/30 transition-all text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    🗑️ Remove Permanently
                  </motion.button>
                </div>
              </div>
            </div>
          </Card>
        )}

        {/* Mobile Cards */}
        <div className="md:hidden space-y-5">
          {loading ? (
            <>
              {[...Array(3)].map((_, i) => (
                <Card key={i}>
                  <div className="p-4">
                    <div className="animate-pulse space-y-3">
                      <div className="h-4 bg-zinc-300 dark:bg-zinc-700 rounded w-2/3"></div>
                      <div className="h-3 bg-zinc-200 dark:bg-zinc-700 rounded w-1/3"></div>
                      <div className="h-12 bg-zinc-200 dark:bg-zinc-700 rounded"></div>
                      <div className="h-10 bg-zinc-300 dark:bg-zinc-600 rounded"></div>
                    </div>
                  </div>
                </Card>
              ))}
            </>
          ) : mobileItems.length === 0 ? (
            <Card>
              <div className="p-6 text-center">
                <div className="h-12 w-12 mx-auto mb-4 rounded-xl bg-zinc-100 dark:bg-zinc-800 flex items-center justify-center">
                  <svg className="h-6 w-6 text-zinc-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-zinc-900 dark:text-zinc-100">No products found</h3>
                <p className="text-sm text-zinc-500 dark:text-zinc-400 mt-2">
                  Try adjusting your filters or add a new product.
                </p>
                {(hasPermission('inventory.create') || user?.role === 'shop_admin' || user?.role === 'superadmin') && (
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={handleAddItem}
                    className="mt-4 w-full px-3 py-2 rounded-xl text-white text-sm font-medium transition-all hover:shadow-lg"
                    style={{ background: ACCENT }}
                  >
                    Add your first product
                  </motion.button>
                )}
              </div>
            </Card>
          ) : (
            mobileItems.map((item) => {
              const stock = getStockConfig(item.totalStock || 0);
              return (
                <Card key={item.id} className="overflow-hidden">
                  <div className="p-4 space-y-4">
                    <div onClick={() => navigate(`/admin/inventory/products/${item.id}`)} className="space-y-1">
                      <h3 className="text-base font-semibold text-zinc-900 dark:text-zinc-100">{item.name}</h3>
                      <p className="text-sm text-zinc-500 dark:text-zinc-400">{item.category || 'Uncategorized'}</p>
                    </div>
                    <div className="flex items-center justify-between rounded-xl bg-white/60 dark:bg-zinc-800/60 border border-white/20 dark:border-white/10 p-3">
                      <div>
                        <p className="text-xs text-zinc-500 dark:text-zinc-400">Quantity</p>
                        <p className={`text-2xl font-semibold ${stock.color}`}>{item.totalStock || 0}</p>
                      </div>
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${stock.chip}`}>
                        {stock.label}
                      </span>
                    </div>
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedItem({ id: item.id, name: item.name, totalStock: item.totalStock || 0 });
                        setShowUpdateModal(true);
                      }}
                    className="w-full px-3 py-2 rounded-xl text-white text-sm font-medium transition-all hover:shadow-lg"
                      style={{ background: ACCENT }}
                    >
                      Update Stock
                    </motion.button>
                  </div>
                </Card>
              );
            })
          )}
        </div>

        {/* Products Table */}
        <Card className="hidden md:block">
          <CardHeader
            title="Products"
            subtitle={`${pagination.total || 0} total products`}
            icon={
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: ACCENT }}>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
              </svg>
            }
          />
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-zinc-500 dark:text-zinc-400 border-b border-white/10 dark:border-white/10">
                  <th className="py-4 px-4 font-medium">
                    <input 
                      type="checkbox" 
                      checked={selectedItems.length === items.length && items.length > 0}
                      onChange={handleSelectAll}
                      className="rounded border-zinc-300 text-blue-600 focus:ring-blue-500" 
                    />
                  </th>
                  <th className="py-4 px-4 font-medium">Product</th>
                  <th className="py-4 px-4 font-medium">Status</th>
                  <th className="py-4 px-4 font-medium">Inventory</th>
                  <th className="py-4 px-4 font-medium">Category</th>
                  <th className="py-4 px-4 font-medium">Supplier</th>
                  <th className="py-4 px-4 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10 dark:divide-white/10">
                {loading ? (
                  <>
                    <SkeletonRow />
                    <SkeletonRow />
                    <SkeletonRow />
                  </>
                ) : items.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="py-12 text-center">
                      <div className="text-center">
                        <div className="h-12 w-12 mx-auto mb-4 rounded-xl bg-zinc-100 dark:bg-zinc-800 flex items-center justify-center">
                          <svg className="h-6 w-6 text-zinc-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                          </svg>
                        </div>
                        <h3 className="text-lg font-medium text-zinc-900 dark:text-zinc-100 mb-2">
                          No products found
                        </h3>
                        <p className="text-zinc-500 dark:text-zinc-400">
                          Try adjusting your filters or add a new product.
                        </p>
                        {(hasPermission('inventory.create') || user?.role === 'shop_admin' || user?.role === 'superadmin') && (
                          <div className="mt-4">
                            <motion.button
                              whileHover={{ scale: 1.02 }}
                              whileTap={{ scale: 0.98 }}
                              onClick={handleAddItem}
                              className="px-4 py-2 rounded-xl text-white font-medium transition-all"
                              style={{ background: ACCENT }}
                            >
                              Add your first product
                            </motion.button>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ) : (
                  items.map((item) => (
                    <motion.tr 
                      key={item.id} 
                      className="text-zinc-800 dark:text-zinc-100 hover:bg-white/30 dark:hover:bg-zinc-800/30 transition-colors"
                      whileHover={{ backgroundColor: 'rgba(255,255,255,0.1)' }}
                    >
                      <td className="py-4 px-4">
                        <input 
                          type="checkbox" 
                          checked={selectedItems.includes(item.id)}
                          onChange={() => handleSelectItem(item.id)}
                          className="rounded border-zinc-300 text-blue-600 focus:ring-blue-500"
                          onClick={(e) => e.stopPropagation()}
                        />
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex items-center space-x-3">
                          <div className="flex-shrink-0">
                            {item.imageUrl ? (
                              <img 
                                className="h-12 w-12 rounded-lg object-cover border border-white/20 dark:border-white/10" 
                                src={item.imageUrl} 
                                alt={item.name}
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.nextSibling.style.display = 'flex';
                                }}
                              />
                            ) : (
                              <div className="h-12 w-12 rounded-lg flex items-center justify-center bg-zinc-200 dark:bg-zinc-700">
                                <svg className="h-6 w-6 text-zinc-400 dark:text-zinc-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                                </svg>
                              </div>
                            )}
                          </div>
                          <div>
                            <div className="font-medium text-zinc-900 dark:text-zinc-100">{item.name}</div>
                            {item.brand && (
                              <div className="text-sm text-zinc-500 dark:text-zinc-400">{item.brand}</div>
                            )}
                            {item.sku && (
                              <div className="text-xs text-zinc-400 dark:text-zinc-500">SKU: {item.sku}</div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <StatusBadge status={item.status || 'draft'} />
                      </td>
                      <td className="py-4 px-4">
                        <div className="text-zinc-800 dark:text-zinc-100">
                          {item.totalStock || 0} in stock
                        </div>
                        <div className="text-sm text-zinc-500 dark:text-zinc-400">
                          Reorder at {item.reorderPoint || 0}
                        </div>
                      </td>
                      <td className="py-4 px-4 text-zinc-800 dark:text-zinc-100">
                        {item.category || '-'}
                      </td>
                      <td className="py-4 px-4 text-zinc-800 dark:text-zinc-100">
                        {item.defaultSupplier?.name || '-'}
                      </td>
                      <td className="py-4 px-4 text-right">
                        <div className="flex items-center gap-2 justify-end">
                          <ActionButton
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedItem({ id: item.id, name: item.name, totalStock: item.totalStock || 0 });
                              setShowUpdateModal(true);
                            }}
                            variant="accent"
                            size="sm"
                            className="!px-4 !py-2"
                          >
                            Update Stock
                          </ActionButton>
                        </div>
                      </td>
                    </motion.tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="p-4 md:p-5 border-t border-white/10 dark:border-white/10">
              <div className="flex items-center justify-between">
                <div className="text-sm text-zinc-600 dark:text-zinc-400">
                  Showing {((pagination.page - 1) * pagination.pageSize) + 1} to {Math.min(pagination.page * pagination.pageSize, pagination.total)} of {pagination.total} results
                </div>
                <div className="flex items-center space-x-2">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => handlePageChange(pagination.page - 1)}
                    disabled={pagination.page === 1}
                    className="px-3 py-2 rounded-lg border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-900 dark:text-zinc-100 hover:bg-white/70 dark:hover:bg-zinc-800/70 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </motion.button>
                  <span className="px-3 py-2 text-sm text-zinc-600 dark:text-zinc-400">
                    Page {pagination.page} of {pagination.totalPages}
                  </span>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => handlePageChange(pagination.page + 1)}
                    disabled={pagination.page === pagination.totalPages}
                    className="px-3 py-2 rounded-lg border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-900 dark:text-zinc-100 hover:bg-white/70 dark:hover:bg-zinc-800/70 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </motion.button>
                </div>
              </div>
            </div>
          )}
        </Card>
      </div>

      {/* Mobile Filters Bottom Sheet */}
      {showMobileFilters && (
        <div className="fixed inset-0 z-50">
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() => setShowMobileFilters(false)}
          />
          <motion.div
            initial={{ y: 40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 40, opacity: 0 }}
            className="absolute bottom-0 left-0 right-0 rounded-t-3xl bg-white/90 dark:bg-zinc-900/90 backdrop-blur-xl border-t border-white/20 dark:border-white/10 shadow-2xl"
          >
            <div className="p-5 space-y-5">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-zinc-900 dark:text-zinc-100">Filters</h3>
                  <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-1">Refine by category and stock</p>
                </div>
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={() => setShowMobileFilters(false)}
                  className="h-9 w-9 rounded-full bg-white/60 dark:bg-zinc-800/60 border border-white/20 dark:border-white/10 flex items-center justify-center text-zinc-600 dark:text-zinc-300"
                  aria-label="Close filters"
                >
                  ×
                </motion.button>
              </div>

              <div>
                <label className="text-sm font-medium text-zinc-700 dark:text-zinc-300">Category</label>
                <select
                  value={filters.category}
                  onChange={(e) => handleFilterChange('category', e.target.value)}
                  className="mt-2 w-full px-4 py-3 rounded-xl border border-white/20 dark:border-white/10 bg-white/60 dark:bg-zinc-800/60 backdrop-blur-sm text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-[--accent]/20 focus:border-[--accent]/30 transition-all min-h-[44px]"
                  style={{ '--accent': ACCENT }}
                >
                  <option value="">All categories</option>
                  {categories.map(category => (
                    <option key={category} value={category}>
                      {category.charAt(0).toUpperCase() + category.slice(1)}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-sm font-medium text-zinc-700 dark:text-zinc-300">Stock status</label>
                <div className="mt-3 grid grid-cols-2 gap-2">
                  {[
                    { id: 'all', label: 'All' },
                    { id: 'in', label: 'In Stock' },
                    { id: 'low', label: 'Low Stock' },
                    { id: 'out', label: 'Out of Stock' }
                  ].map(option => (
                    <motion.button
                      key={option.id}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => setMobileStockStatus(option.id)}
                    className={`px-3 py-2 rounded-xl text-sm font-medium border ${
                        mobileStockStatus === option.id
                          ? 'bg-[--accent] text-white border-transparent shadow-lg'
                          : 'bg-white/60 dark:bg-zinc-800/60 text-zinc-700 dark:text-zinc-300 border-white/20 dark:border-white/10'
                      }`}
                      style={{ '--accent': ACCENT }}
                    >
                      {option.label}
                    </motion.button>
                  ))}
                </div>
              </div>

              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setShowMobileFilters(false)}
                className="w-full px-3 py-2 rounded-xl text-white text-sm font-medium transition-all hover:shadow-lg"
                style={{ background: ACCENT }}
              >
                Apply Filters
              </motion.button>
            </div>
          </motion.div>
        </div>
      )}

      <UpdateStockModal
        isOpen={showUpdateModal}
        onClose={() => setShowUpdateModal(false)}
        onSuccess={loadItems}
        item={selectedItem}
      />

      {/* Add Supplier Modal */}
      {showSupplierModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="p-6 rounded-2xl max-w-md w-full mx-4 bg-white/90 dark:bg-zinc-900/90 backdrop-blur-xl border border-white/20 dark:border-white/10 shadow-2xl"
          >
            <div className="flex items-center mb-4">
              <div className="h-10 w-10 rounded-xl bg-[--accent]/10 flex items-center justify-center mr-3" style={{ '--accent': ACCENT }}>
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: ACCENT }}>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-zinc-900 dark:text-zinc-100">
                Add Supplier
              </h3>
            </div>
            
            <form onSubmit={(e) => {
              e.preventDefault();
              const formData = new FormData(e.target);
              const supplierData = {
                name: formData.get('name'),
                contactName: formData.get('contactName'),
                email: formData.get('email'),
                phone: formData.get('phone'),
                terms: formData.get('terms'),
                notes: formData.get('notes')
              };
              handleSaveSupplier(supplierData);
            }}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2">Name *</label>
                  <input
                    type="text"
                    name="name"
                    required
                    className="w-full px-4 py-3 rounded-xl border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-[--accent]/20 focus:border-[--accent]/30 transition-all"
                    style={{ '--accent': ACCENT }}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2">Contact Name</label>
                  <input
                    type="text"
                    name="contactName"
                    className="w-full px-4 py-3 rounded-xl border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-[--accent]/20 focus:border-[--accent]/30 transition-all"
                    style={{ '--accent': ACCENT }}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2">Email</label>
                  <input
                    type="email"
                    name="email"
                    className="w-full px-4 py-3 rounded-xl border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-[--accent]/20 focus:border-[--accent]/30 transition-all"
                    style={{ '--accent': ACCENT }}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2">Phone</label>
                  <input
                    type="tel"
                    name="phone"
                    className="w-full px-4 py-3 rounded-xl border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-[--accent]/20 focus:border-[--accent]/30 transition-all"
                    style={{ '--accent': ACCENT }}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2">Terms</label>
                  <input
                    type="text"
                    name="terms"
                    placeholder="e.g., Net 30"
                    className="w-full px-4 py-3 rounded-xl border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-[--accent]/20 focus:border-[--accent]/30 transition-all"
                    style={{ '--accent': ACCENT }}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2">Notes</label>
                  <textarea
                    name="notes"
                    rows={3}
                    className="w-full px-4 py-3 rounded-xl border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-[--accent]/20 focus:border-[--accent]/30 transition-all resize-none"
                    style={{ '--accent': ACCENT }}
                  />
                </div>
              </div>
              
              <div className="flex justify-end gap-3 mt-6">
                <motion.button
                  type="button"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleCancelSupplier}
                  className="px-4 py-2 rounded-xl border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-700 dark:text-zinc-300 hover:bg-white/70 dark:hover:bg-zinc-800/70 transition-all"
                >
                  Cancel
                </motion.button>
                <motion.button
                  type="submit"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="px-4 py-2 rounded-xl text-white font-medium transition-all duration-200 hover:shadow-lg"
                  style={{ background: ACCENT }}
                >
                  Add Supplier
                </motion.button>
              </div>
            </form>
          </motion.div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="p-6 rounded-2xl shadow-2xl max-w-lg w-full mx-4 bg-white/90 dark:bg-zinc-900/90 backdrop-blur-xl border border-white/20 dark:border-white/10"
          >
            <div className="flex items-center mb-4">
              <div className="h-10 w-10 rounded-xl bg-red-100 dark:bg-red-900/30 flex items-center justify-center mr-3">
                <svg className="h-5 w-5 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-zinc-900 dark:text-zinc-100">
                Permanently Delete Products
              </h3>
            </div>
            
            <div className="mb-6 space-y-4">
              <p className="text-sm text-zinc-600 dark:text-zinc-400">
                You are about to <strong className="text-red-600 dark:text-red-400">permanently delete {selectedItems.length} product(s)</strong>.
              </p>
              
              {/* Warning */}
              <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-xl border border-red-200 dark:border-red-800">
                <div className="flex items-start gap-3">
                  <svg className="h-6 w-6 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                  <div>
                    <p className="text-red-800 dark:text-red-200 font-bold text-sm mb-1">
                      ⚠️ THIS ACTION CANNOT BE UNDONE
                    </p>
                    <ul className="text-red-700 dark:text-red-300 text-xs space-y-1 list-disc list-inside">
                      <li>Products will be permanently deleted</li>
                      <li>All inventory data will be lost</li>
                      <li>Transaction history will be affected</li>
                      <li>This includes products with stock</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Alternative suggestion */}
              <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-xl border border-blue-200 dark:border-blue-800">
                <div className="flex items-start gap-2">
                  <svg className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div>
                    <p className="text-blue-800 dark:text-blue-200 font-medium text-sm">
                      💡 Alternative: Use "📦 Deactivate (Archive)" Instead
                    </p>
                    <p className="text-blue-700 dark:text-blue-300 text-xs mt-1">
                      Archiving keeps products inactive but preserves all data and can be reversed.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="flex gap-3">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setShowDeleteConfirm(false)}
                className="flex-1 px-4 py-3 rounded-xl border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-700 dark:text-zinc-300 hover:bg-white/70 dark:hover:bg-zinc-800/70 transition-all font-medium"
              >
                Cancel
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleBulkDelete}
                disabled={saving}
                className="flex-1 px-4 py-3 bg-red-600 hover:bg-red-700 text-white rounded-xl transition-all shadow-lg disabled:opacity-50 disabled:cursor-not-allowed font-bold"
              >
                {saving ? '🗑️ Deleting...' : `🗑️ Yes, Delete Forever`}
              </motion.button>
            </div>
          </motion.div>
        </div>
      )}
    </InventoryLayout>
  );
};

export default Products;
